import React, { forwardRef, useEffect } from 'react';
import moment from 'moment';
import { styled, Tooltip } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import Avatar from 'shell/components/Avatar';
import Box from 'shell/components/Box';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Link from 'shell/components/Link';
import ResourceChip from 'shell/components/ResourceChip';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';
import theme from 'shell/themes';
import { downloadFileFromURL, getS3FileURL, getUserPreferredFullName } from 'utils/helper';

// override the default getUserPreferredFullName behavior to display
// the name of a user as [Removed] if they have been removed
// added in conjunction with _getUserById
const _getUserPreferredFullName = (user) => {
    if (user.isRemoved) return '[Removed]';
    return getUserPreferredFullName(user);
};

const groupMessagesBySender = (messages) => {
    const result = [];
    let currentBlock = [];

    for (let i = 0; i < messages.length; i++) {
        const currentMessage = messages[i];
        const previousMessage = currentBlock.length > 0 ? currentBlock[currentBlock.length - 1] : null;

        if (previousMessage && previousMessage.sender_account_id !== currentMessage.sender_account_id) {
            result.push(currentBlock);
            currentBlock = [];
        }

        currentBlock.push(currentMessage);
    }

    if (currentBlock.length > 0) {
        result.push(currentBlock);
    }

    return result;
};

const ChatMessage = forwardRef(({ index, isAuthorSelf, getUserById, message, position, onLoad, ...rest }, ref) => {
    const { device } = useViewport();

    let replyMessageText = message.replied_message?.active ? message.replied_message.message_text : '[Deleted]';
    if (message.replied_message) {
        if (message.replied_message.type === 'file' && !message.replied_message.message_text) {
            replyMessageText = '[Attachment]';
        }
        if (message.replied_message.type === 'photo' && !message.replied_message.message_text) {
            replyMessageText = '[Image]';
        }
    }

    const handleSelect = ({ key }, file) => {
        switch (key) {
            case 'download':
                downloadFileFromURL(getS3FileURL(file.data), file.data.name);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (message.type === 'photo') {
            const img = new Image();
            img.onload = onLoad;
            img.src = message.photoURL;
        }
    }, [message]);

    if (isAuthorSelf) {
        return (
            <MessageRoot device={device} isSelf>
                {message.replied_message && (
                    <ReplyStack isSelf spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} spacing={1}>
                            <ReplyIcon kind={'reply'} />
                            <Text color={'light'} variant={'p2'}>
                                Replied to{' '}
                                {getUserById(message.replied_message.sender_account_id).role === 'instructor' && (
                                    <>
                                        <Tag component={'span'} type={'role'} value={'instructor'} />{' '}
                                    </>
                                )}
                                {_getUserPreferredFullName(getUserById(message.replied_message.sender_account_id))}
                            </Text>
                        </Stack>
                        <ReplyBubble alignItems={'center'} direction={'row'} spacing={1}>
                            <Text color={'light'} rich variant={'p2'}>
                                {replyMessageText}
                            </Text>
                        </ReplyBubble>
                    </ReplyStack>
                )}
                {message.message_text && (
                    <MessageBubble ref={ref} isSelf $position={position} {...rest}>
                        <Text color={'inherit'} rich variant={'p2'}>
                            {message.message_text}
                        </Text>
                    </MessageBubble>
                )}
                {message.type === 'file' && (
                    <CustomResourceChip
                        options={[{ key: 'download', value: 'Download' }]}
                        resource={message.file}
                        size={'small'}
                        type={'file'}
                        onSelect={handleSelect}
                        {...(!message.message_text && {
                            ref,
                            ...rest,
                        })}
                    />
                )}
                {message.type === 'photo' && (
                    <CustomLink
                        external
                        href={message.photoURL}
                        rel={'noopener noreferrer'}
                        target={'_blank'}
                        {...(!message.message_text && {
                            ref,
                            ...rest,
                        })}
                    >
                        <img alt={'image for message'} src={message.photoURL} />
                    </CustomLink>
                )}
            </MessageRoot>
        );
    }

    return (
        <MessageRoot alignItems={'flex-end'} device={device} direction={'row'} spacing={1}>
            {position === 'single' || position === 'end' ? (
                <Avatar size={'small'} user={getUserById(message.sender_account_id)} />
            ) : (
                <AvatarPlaceholder />
            )}
            <Stack sx={{ minWidth: 0 }}>
                {message.replied_message && (
                    <ReplyStack spacing={1}>
                        {index !== 0 && (
                            <Stack alignItems={'center'} direction={'row'} spacing={1}>
                                <ReplyIcon kind={'reply'} />
                                <Text color={'light'} variant={'p2'}>
                                    Replied to{' '}
                                    {getUserById(message.replied_message.sender_account_id).role === 'instructor' && (
                                        <>
                                            <Tag component={'span'} type={'role'} value={'instructor'} />{' '}
                                        </>
                                    )}
                                    {_getUserPreferredFullName(getUserById(message.replied_message.sender_account_id))}
                                </Text>
                            </Stack>
                        )}
                        <ReplyBubble alignItems={'center'} direction={'row'} spacing={1}>
                            <Text color={'light'} rich variant={'p2'}>
                                {replyMessageText}
                            </Text>
                        </ReplyBubble>
                    </ReplyStack>
                )}
                {message.message_text && (
                    <MessageBubble ref={ref} $position={position} {...rest}>
                        <Text rich variant={'p2'}>
                            {message.message_text}
                        </Text>
                    </MessageBubble>
                )}
                {message.type === 'file' && (
                    <CustomResourceChip
                        options={[{ key: 'download', value: 'Download' }]}
                        resource={message.file}
                        size={'small'}
                        type={'file'}
                        onSelect={handleSelect}
                        {...(!message.message_text && {
                            ref,
                            ...rest,
                        })}
                    />
                )}
                {message.type === 'photo' && (
                    <CustomLink
                        external
                        href={message.photoURL}
                        rel={'noopener noreferrer'}
                        target={'_blank'}
                        {...(!message.message_text && {
                            ref,
                            ...rest,
                        })}
                    >
                        <img alt={'image for message'} src={message.photoURL} />
                    </CustomLink>
                )}
            </Stack>
        </MessageRoot>
    );
});

const ChatMessageBlock = forwardRef(
    (
        { messages, getUserById, selfOwnerAccountId, showSenderName, onClickDelete, onClickReply, onLoad, ...rest },
        ref
    ) => {
        const timestamp = messages[0].created_on;
        const groups = groupMessagesBySender(messages);

        // override the getUserById function
        // if no user can be found, return a dummy instead
        // this happens when the given id belongs to an account that is inactive
        const _getUserById = (id) => {
            const accountUser = getUserById(id);
            if (accountUser) return accountUser;
            return {
                isRemoved: true,
                role: 'student',
                first_name: 'Removed',
                last_name: '',
                display_name: '',
                profile_circle: {
                    bg_color: theme.palette.ensightful.neutral[200],
                    text_color: theme.palette.ensightful.neutral[900],
                },
            };
        };

        const renderSenderName = (message) => {
            if (message.sender_account_id === selfOwnerAccountId || !showSenderName) return null;

            if (message.replied_message) {
                return (
                    <SenderName color={'light'} isReply variant={'p2'}>
                        <Stack
                            sx={{ flexWrap: 'wrap' }}
                            component={'span'}
                            alignItems={'center'}
                            direction={'row'}
                            spacing={1}
                        >
                            <Icon sx={{ fontSize: '1rem' }} kind={'reply'} />
                            {_getUserById(message.sender_account_id).role === 'instructor' && (
                                <>
                                    <Tag component={'span'} type={'role'} value={'instructor'} />{' '}
                                </>
                            )}
                            {_getUserPreferredFullName(_getUserById(message.sender_account_id))} replied to{' '}
                            {_getUserById(message.replied_message.sender_account_id).role === 'instructor' && (
                                <>
                                    <Tag component={'span'} type={'role'} value={'instructor'} />{' '}
                                </>
                            )}
                            {_getUserPreferredFullName(_getUserById(message.replied_message.sender_account_id))}
                        </Stack>
                    </SenderName>
                );
            }

            return (
                <SenderName color={'light'} variant={'p2'}>
                    {_getUserById(message.sender_account_id).role === 'instructor' && (
                        <>
                            <Tag component={'span'} type={'role'} value={'instructor'} />{' '}
                        </>
                    )}
                    {_getUserPreferredFullName(_getUserById(message.sender_account_id))}
                </SenderName>
            );
        };

        const getPosition = (groupLength, index) => {
            if (groupLength === 1) return 'single';
            if (index === 0) return 'start';
            if (index === groupLength - 1) return 'end';
            return 'middle';
        };

        const renderGroup = (group, index) => {
            return (
                <Stack sx={{ width: '100%' }} key={index} spacing={0.5}>
                    {renderSenderName(group[0])}
                    {group.map((message, index) => {
                        const isAuthorSelf = selfOwnerAccountId === message.sender_account_id;
                        return (
                            <CustomTooltip
                                key={`${message.id}-${index}`}
                                arrow={false}
                                enterTouchDelay={100}
                                title={
                                    <Stack direction={isAuthorSelf ? 'row' : 'row-reverse'}>
                                        {isAuthorSelf && (
                                            <IconButton
                                                kind={'delete'}
                                                size={'small'}
                                                variant={'square'}
                                                onClick={() => onClickDelete(message)}
                                            />
                                        )}
                                        <IconButton
                                            data-cy={'reply-button'}
                                            kind={'reply'}
                                            size={'small'}
                                            variant={'square'}
                                            onClick={() => onClickReply(message)}
                                        />
                                    </Stack>
                                }
                                placement={isAuthorSelf ? 'left' : 'right'}
                            >
                                <ChatMessage
                                    index={index}
                                    isAuthorSelf={isAuthorSelf}
                                    getUserById={_getUserById}
                                    message={message}
                                    position={getPosition(group.length, index)}
                                    onLoad={onLoad}
                                />
                            </CustomTooltip>
                        );
                    })}
                </Stack>
            );
        };

        return (
            <Stack ref={ref} sx={{ mt: 4 }} alignItems={'center'} spacing={4} {...rest}>
                <Text color={'light'} variant={'p3'}>
                    {moment(timestamp).calendar()}
                </Text>
                {groups.map(renderGroup)}
            </Stack>
        );
    }
);

const SenderName = styled(Text)(({ isReply, theme }) => {
    return {
        marginLeft: `calc(1.5rem + ${theme.spacing(1)})`,
        ...(isReply && {
            transform: `translateY(${theme.spacing(2)})`,
        }),
    };
});

const CustomTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }) => ({
        ['& .MuiTooltip-tooltip']: {
            marginLeft: `${theme.spacing(-1)} !important`,
            marginRight: `${theme.spacing(-1)} !important`,
            backgroundColor: 'transparent',
        },
    })
);

const MessageRoot = styled(Stack)(({ device, isSelf }) => {
    return {
        alignSelf: isSelf ? 'flex-end' : 'flex-start',
        maxWidth: device === 'mobile' ? 'max(75%, 200px)' : 'max(55%, 200px)',
    };
});

const MessageBubble = styled(Box)(({ isSelf, $position, theme }) => {
    return {
        zIndex: 1,
        padding: theme.spacing(2),
        ...(isSelf
            ? {
                  background: theme.palette.ensightful.primary[500],
                  color: theme.palette.ensightful.shades[0],
                  ...($position === 'single' && {
                      borderRadius: '12px 12px 12px 12px',
                  }),
                  ...($position === 'start' && {
                      borderRadius: '12px 12px 4px 12px',
                  }),
                  ...($position === 'middle' && {
                      borderRadius: '12px 4px 4px 12px',
                  }),
                  ...($position === 'end' && {
                      borderRadius: '12px 4px 12px 12px',
                  }),
                  '& a': {
                      color: theme.palette.ensightful.shades[0],
                      textDecoration: 'underline',
                      transition: 'none',
                      '&:hover': {
                          color: theme.palette.ensightful.neutral[50],
                          textDecoration: 'underline',
                      },
                      '&:active': {
                          color: theme.palette.ensightful.neutral[100],
                          textDecoration: 'underline',
                      },
                  },
              }
            : {
                  background: theme.palette.ensightful.neutral[200],
                  ...($position === 'single' && {
                      borderRadius: '12px 12px 12px 12px',
                  }),
                  ...($position === 'start' && {
                      borderRadius: '12px 12px 12px 4px',
                  }),
                  ...($position === 'middle' && {
                      borderRadius: '4px 12px 12px 4px',
                  }),
                  ...($position === 'end' && {
                      borderRadius: '4px 12px 12px 12px',
                  }),
              }),
    };
});

const AvatarPlaceholder = styled(Box)(() => {
    return {
        minWidth: '1.5rem',
        maxWidth: '1.5rem',
        minHeight: '1.5rem',
        maxHeight: '1.5rem',
    };
});

const ReplyStack = styled(Stack)(({ isSelf, theme }) => {
    return {
        transform: `translateY(${theme.spacing(2)})`,
        ...(isSelf && {
            alignSelf: 'flex-end',
            width: 'fit-content',
        }),
    };
});

const ReplyIcon = styled(Icon)(() => {
    return {
        fontSize: '1rem',
    };
});

const ReplyBubble = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '12px',
        padding: theme.spacing(2, 2, 3),
        background: theme.palette.ensightful.neutral[100],
    };
});

const CustomLink = styled(Link)(({ theme }) => {
    return {
        zIndex: 1,
        marginTop: theme.spacing(0.5),
        '& > img': {
            borderRadius: '12px',
            width: '100%',
        },
    };
});

const CustomResourceChip = styled(ResourceChip)(({ theme }) => {
    return {
        marginTop: theme.spacing(0.5),
    };
});

export default ChatMessageBlock;
